import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { BaseApiService } from '@app/core/services/api/base-api/base-api.service';
import { API_CONSTANTS } from '@app/core/constants/api.constants';
import { IOnboardingStatus, IOnboardingSteps } from '../interfaces/onboarding.interface';

/**
 * Getting-started checklist of the signed-in coach. Keeps the last known status so the
 * checklist and the pages completing its steps share one source of truth.
 * Failures stay silent (see `API_CONSTANTS.SILENT_URLS`): the checklist is a nice-to-have.
 */
@Injectable({
    providedIn: 'root',
})
export class OnboardingService extends BaseApiService {
    private readonly statusSubject$ = new BehaviorSubject<IOnboardingStatus | null>(null);
    readonly status$: Observable<IOnboardingStatus | null> = this.statusSubject$.asObservable();

    constructor(http: HttpClient) {
        super(http, API_CONSTANTS.ENDPOINTS.ONBOARDING);
    }

    /** Re-reads the status, e.g. after the coach added a player or a match. */
    refresh(): void {
        this.get<IOnboardingStatus>().subscribe({ next: (status) => this.statusSubject$.next(status), error: () => {} });
    }

    /** Re-reads the status after the coach did something that may tick `step`, while that step is still open. */
    refreshIfOpen(step: keyof IOnboardingSteps): void {
        const current = this.statusSubject$.value;
        if (current && !current.dismissed && !current.steps[step]) {
            this.refresh();
        }
    }

    /** Ticks "check the training history"; a no-op once it is ticked. */
    markHistoryViewed(): void {
        if (this.statusSubject$.value?.steps.historyViewed) {
            return;
        }
        this.post<IOnboardingStatus>('/history-viewed', {}).subscribe({
            next: (status) => this.statusSubject$.next(status),
            error: () => {},
        });
    }

    /** Hides the checklist right away and for good. */
    dismiss(): void {
        const current = this.statusSubject$.value;
        if (current) {
            this.statusSubject$.next({ ...current, dismissed: true });
        }
        this.post<IOnboardingStatus>('/dismiss', {}).subscribe({ error: () => {} });
    }

    /** Forgets the status when the coach signs out. */
    clear(): void {
        this.statusSubject$.next(null);
    }
}
